/** @jsx jsx */
import * as React from 'react';
import { START_DATE, useDay } from './useDatePicker';
import { jsx, useTheme } from '@front10/base-elements';
import { DatePickerContext } from './datePickerContext';
import { addDays } from 'date-fns';
import Box from '../../elements/Box';
import { BlackoutDates } from './../../containers/VerticalBookingContainer/blackoutDates';
import { weekDaysLong } from '../../../helpers/localization/localizationUtils';
import { Context } from '@front10/helpers/dist/Context';
import { formatDate } from '../utils';
import { useIsMobileView, useTranslation } from '@front10/utils';

type GetColorProps = {
  selectedFirstOrLastColor: string;
  normalColor: string;
  selectedColor: string;
  rangeHoverColor: string;
  disabledColor: string;
};

function getColor(
  isSelected: boolean,
  isSelectedStartOrEnd: boolean,
  isWithinHoverRange: boolean,
  isDisabled: boolean
) {
  return ({
    selectedFirstOrLastColor,
    normalColor,
    selectedColor,
    rangeHoverColor,
    disabledColor
  }: GetColorProps) => {
    if (isSelectedStartOrEnd) {
      return selectedFirstOrLastColor;
    } else if (isSelected) {
      return selectedColor;
    } else if (isDisabled) {
      return disabledColor;
    } else if (isWithinHoverRange) {
      return rangeHoverColor;
    } else {
      return normalColor;
    }
  };
}

type DayProps = {
  dayLabel?: string;
  date: Date;
  blackoutDatesIntegration?: boolean;
  fetchBlackoutDates?: BlackoutDates | null;
  focusedInput?: string | null;
  allowHoverStyleInRange?: boolean;
};

export function isSameDay(d1: Date, d2?: Date | null): boolean {
  if (!d1 || !d2) {
    return false;
  }
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}

export function Day({ dayLabel, date, allowHoverStyleInRange, ...rest }: DayProps) {
  const dayRef = React.useRef<HTMLDivElement>(null);
  const {
    theme: { text, layout }
  } = useTheme();
  const {
    dateFormat,
    focusedDate,
    selectPhase,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    isStartDate,
    isEndDate,
    getMinimumEnabledDate
  } = React.useContext(DatePickerContext);

  const isSameDay_v = React.useMemo(() => isSameDay(date, focusedDate), [date, focusedDate]);

  const {
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    disabledDate,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex,
    isSelectedStartOrEndAndBlackout,
    isAMiddleAndBlackout
  } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    // setIsDayFocused,
    dayRef,
    blackoutDatesIntegration: rest?.blackoutDatesIntegration,
    fetchBlackoutDates: rest?.fetchBlackoutDates,
    focusedInput: rest?.focusedInput,
    getMinimumEnabledDate
  });

  const context = React.useContext(Context);
  const { localizationData } = context;
  const translate = useTranslation();
  const daysLabels = weekDaysLong(translate, localizationData);
  const isMobile = useIsMobileView();

  React.useEffect(() => {
    if (isSameDay_v && !disabledDate) {
      dayRef.current?.focus();
    }
  }, [isSameDay_v, disabledDate]);

  if (!dayLabel) {
    return <div />;
  }
  const getColorFn = getColor(isSelected, isSelectedStartOrEnd, isWithinHoverRange, disabledDate);

  const styles = layout?.datepickerStyles as any;

  const dayVariant = function getVariant() {
    if (layout && text) {
      if (isAMiddleAndBlackout) {
        return styles?.dayMiddleAndBlackoutDay ?? text.fontSize5;
      } else if (isSelectedStartOrEndAndBlackout) {
        return styles?.dayStartOrEndAnaBlackoutDay ?? text.fontSize5;
      } else if (isSelectedStartOrEnd) {
        return styles?.dayStartOrEnd ?? text.fontSize5;
      } else if (isSelected && disabledDate) {
        return styles?.dayDisabled || styles?.daySelected
          ? { ...styles?.daySelected, ...styles?.dayDisabled }
          : text.fontSize5;
      } else if (isSelected) {
        return styles?.daySelected ?? text.fontSize5;
      } else if (isWithinHoverRange) {
        return styles?.dayWithinRange ?? text.fontSize5;
      } else if (disabledDate) {
        return styles?.dayDisabled ?? text.fontSize5;
      } else {
        return styles?.dayNormal ?? text.fontSize5;
      }
    }
  };

  const bg = getColorFn({
    selectedFirstOrLastColor: 'primary',
    normalColor: '#fff',
    selectedColor: 'primary',
    rangeHoverColor: 'primary',
    disabledColor: '#fff'
  });

  const tomorrow = addDays(date, 1);
  const isDayInRange = isSelected || isSelectedStartOrEnd || isWithinHoverRange;

  const styleStartOrEnd = () => {
    if (layout) {
      if (isStartDate(date)) {
        return {
          zIndex: 1,
          ...styles?.dayStart
        };
      } else if (isEndDate(date) && !isStartDate(date)) {
        return {
          zIndex: 1,
          ...styles?.dayEnd
        };
      } else {
        return '';
      }
    }
    return '';
  };

  const getAriaLabel = () => {
    const formatedDate = formatDate(date, dateFormat);
    const dayName = date ? daysLabels[date?.getDay()] : '';
    const prefixArialLabel = !isDateBlocked(date)
      ? translate('date-range-picker-date-available', `Choose {stage} date`, {
          stage: selectPhase === START_DATE ? 'departure' : 'return'
        })
      : translate('date-range-picker-date-not-available', `Not available`);

    return `${prefixArialLabel} ${dayName} ${formatedDate}`;
  };

  return (
    <Box
      className="cell calendar-day"
      sx={{
        width: isMobile ? '100%' : 'auto',
        ...styleStartOrEnd()
      }}
    >
      <Box
        onClick={() => {
          onClick();
          onDateFocus(date);
        }}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        tabIndex={disabledDate ? -1 : tabIndex}
        role="gridcell"
        ariaLabel={getAriaLabel()}
        ariaSelected={isDateSelected(date)}
        focusReferences={dayRef}
        sx={{
          padding: '2',
          margin: 0,
          border: '0',
          width: '100%',
          height: '38px',
          minWidth: '38px',
          variant: 'text.fontSize5',
          outline: 'none',
          color: getColorFn({
            selectedFirstOrLastColor: '#fff',
            normalColor: 'text',
            selectedColor: '#fff',
            rangeHoverColor: '#fff',
            disabledColor: 'disabledDayColor'
          }),
          bg,
          ...(!disabledDate &&
            (allowHoverStyleInRange || (!allowHoverStyleInRange && !isDayInRange)) && {
              '&:hover': {
                borderRadius: 'default',
                borderWidth: 1,
                borderStyle: 'solid',
                position: 'relative',
                bg: 'white',
                color: 'text',
                boxShadow: 'inset 0 0 0 1px',
                variant: 'layout.datepickerStyles.buttonsHover'
              },
              '&:focus': {
                borderRadius: 'default',
                borderWidth: 1,
                borderStyle: 'solid',
                position: 'relative',
                bg: 'white',
                color: 'text',
                boxShadow: 'inset 0 0 0 1px',
                variant: 'layout.datepickerStyles.buttonsHover'
              }
            }),
          ...dayVariant(),
          ...(isStartDate(date) && {
            borderTopLeftRadius: 'default',
            borderBottomLeftRadius: 'default'
          }),
          ...(!isDateSelected(tomorrow) &&
            !isDateHovered(tomorrow) && {
              borderTopRightRadius: 'default',
              borderBottomRightRadius: 'default'
            })
        }}
      >
        {dayLabel}
      </Box>
    </Box>
  );
}
